// Bounce Back runner.
//
// Gross-only. A "bounce back" is a strong hole played immediately after a
// double bogey or worse. Settlement is the same per-opponent transfer model
// as Bingo Bango Bongo / Putting: every point a player earns collects
// `betPerPoint` from each opponent, so in a fourball at $1 a par-bounce-back
// is +$3 to the player and −$1 to each of the three others. Zero-sum.
//
// Per-opponent rates (current hole, when the immediately preceding scored
// hole was a double bogey or worse):
//   • BB Par  — exactly par                  → +1   (bbPar)
//   • BB Bird — birdie or better             → +2   (bbBird)
//   • BB Dbl  — another double bogey or worse → −1   (bbDbl, player PAYS)
// A bogey (1 over) after a double bogey is neither a bounce-back nor a
// back-to-back double — no event fires.
//
// Per-category buckets carry the player's OWN event count + holes (for the
// click-to-expand list) and their NET money for that category. The round
// total is the sum of the three category nets. Pure function — no state.

import { Course, Player, ScoreMap } from "../types";

export type BounceBackCategory = "bbPar" | "bbBird" | "bbDbl";
const CATEGORIES: BounceBackCategory[] = ["bbPar", "bbBird", "bbDbl"];

// Per-opponent rate for each category (also the sign of the transfer).
const RATE: Record<BounceBackCategory, number> = {
  bbPar: 1,
  bbBird: 2,
  bbDbl: -1,
};

export interface BounceBackOptions {
  betPerPoint?: number;
}

export interface BounceBackBucket {
  // This player's own events in the category.
  count: number;
  // 1-based hole numbers where this player's event fired.
  holes: number[];
  // Net money for this player in this category (own events − losses to
  // others' events of the same category).
  winnings: number;
}

export interface BounceBackPayout {
  total: number;
  bbPar: BounceBackBucket;
  bbBird: BounceBackBucket;
  bbDbl: BounceBackBucket;
}

export interface BounceBackResult {
  payouts: Record<string, BounceBackPayout>;
  hasAnyBet: boolean;
}

export interface BounceBackInput {
  course: Course;
  players: Player[];
  scores: ScoreMap;
  options: BounceBackOptions;
}

function emptyBucket(): BounceBackBucket {
  return { count: 0, holes: [], winnings: 0 };
}

// Which bounce-back category fires for a hole, given the current hole's
// gross-vs-par delta. Caller has already confirmed the previous scored
// hole was a double bogey or worse. Returns null for a bogey (no event).
function categoryFor(delta: number): BounceBackCategory | null {
  if (delta <= -1) return "bbBird"; // birdie or better
  if (delta === 0) return "bbPar"; // exactly par
  if (delta >= 2) return "bbDbl"; // back-to-back double bogey or worse
  return null; // bogey — no event
}

export function runBounceBack(input: BounceBackInput): BounceBackResult {
  const { course, players, scores, options } = input;
  const bet = Number(options.betPerPoint) || 0;

  const payouts: Record<string, BounceBackPayout> = {};
  for (const p of players) {
    payouts[p.id] = {
      total: 0,
      bbPar: emptyBucket(),
      bbBird: emptyBucket(),
      bbDbl: emptyBucket(),
    };
  }

  if (bet <= 0 || players.length < 2) {
    return { payouts, hasAnyBet: false };
  }

  const holes = [...course.holes].sort((a, b) => a.number - b.number);

  for (const p of players) {
    // Delta (gross − par) of the immediately preceding hole, or null when
    // that hole had no score (so "following" only links consecutive holes
    // that were both actually played).
    let prevDelta: number | null = null;

    for (const hole of holes) {
      const sc = scores[p.id]?.[hole.number];
      const gross =
        sc && typeof sc.gross === "number" ? sc.gross : null;

      if (gross === null) {
        prevDelta = null;
        continue;
      }

      const delta = gross - hole.par;

      // Fire only when the previous scored hole was a double bogey or worse.
      if (prevDelta !== null && prevDelta >= 2) {
        const cat = categoryFor(delta);
        if (cat) {
          const rate = RATE[cat];
          const opponents = players.filter((op) => op.id !== p.id);
          if (opponents.length > 0) {
            const bucket = payouts[p.id][cat];
            bucket.count += 1;
            bucket.holes.push(hole.number);
            bucket.winnings += rate * bet * opponents.length;
            for (const op of opponents) {
              payouts[op.id][cat].winnings -= rate * bet;
            }
          }
        }
      }

      prevDelta = delta;
    }
  }

  for (const p of players) {
    const pay = payouts[p.id];
    for (const cat of CATEGORIES) pay[cat].holes.sort((a, b) => a - b);
    pay.total = pay.bbPar.winnings + pay.bbBird.winnings + pay.bbDbl.winnings;
  }

  return { payouts, hasAnyBet: true };
}
